/* Smith Protective — Email Blacklist page (Iter208).
 *
 * Route: /smith/blacklist (super_admin + master_demo can view, super
 * only can add/remove). Lists auto-blacklisted emails (3 consecutive
 * failures) + manual entries, with CSV export.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ban, Download, Plus, RefreshCw, Search, Trash2, ExternalLink, Loader2 } from "lucide-react";

import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import OutreachTabBar from "./OutreachTabBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmithBlacklist() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addReason, setAddReason] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200", page: "1" });
      if (q.trim()) params.set("q", q.trim());
      const { data } = await axios.get(
        `${API}/smith/outreach/blacklist?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Falha ao carregar blacklist");
    } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const addManual = async () => {
    if (!addEmail.includes("@")) { toast.error("Email inválido"); return; }
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/smith/outreach/blacklist`,
        { email: addEmail.trim(), reason: addReason.trim() || null });
      toast.success(data.reactivated ? "Entrada reativada" : "Adicionado à blacklist");
      setAddOpen(false); setAddEmail(""); setAddReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || "Falha ao adicionar");
    } finally { setAdding(false); }
  };
  const remove = async (row) => {
    if (!confirm(`Remover ${row.email} da blacklist?`)) return;
    try {
      await axios.delete(`${API}/smith/outreach/blacklist/${row.id}`);
      toast.success("Removido da blacklist");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || "Falha ao remover");
    }
  };
  const exportCsv = () => {
    // Trigger CSV download by opening the endpoint URL with the token in
    // Authorization header via fetch, then convert to blob.
    (async () => {
      try {
        const res = await fetch(`${API}/smith/outreach/blacklist/export`, {
          headers: {
            Authorization: (axios.defaults.headers.common?.Authorization) || "",
          },
        });
        if (!res.ok) throw new Error("export failed");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `smith-blacklist-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("CSV baixado");
      } catch (e) { toast.error("Falha no export CSV"); }
    })();
  };

  return (
    <div className="flex h-screen bg-[#0B1120]">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <AppHeader />
        <div className="px-6 pt-3">
          <OutreachTabBar current="blacklist" />
        </div>
        <main className="flex-1 overflow-y-auto p-6" data-testid="smith-blacklist-page">
          <header className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg,#DC2626 0%,#991B1B 100%)" }}>
                <Ban className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-[20px] font-black text-white">Email Blacklist</h1>
                <p className="text-[12px] text-slate-400">
                  {total} email(s) bloqueado(s) — auto após 3 falhas ou manual.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} data-testid="blacklist-refresh"
                      className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-[12px] font-bold text-white inline-flex items-center gap-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin":""}`} /> Atualizar
              </button>
              <button onClick={exportCsv} data-testid="blacklist-export-csv"
                      className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-[12px] font-bold text-white inline-flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
              <button onClick={() => setAddOpen(true)} data-testid="blacklist-add-open"
                      className="h-9 px-3 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5"
                      style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                <Plus className="w-3.5 h-3.5" /> Adicionar
              </button>
            </div>
          </header>

          <div className="mb-4 relative max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Buscar email…"
                   data-testid="blacklist-search"
                   className="w-full h-9 pl-8 pr-3 rounded-lg bg-black/30 border border-white/10 text-[12.5px] text-white focus:border-[#DC2626] outline-none" />
          </div>

          <div className="rounded-2xl border border-white/10 overflow-hidden"
               style={{ background: "rgba(15,23,42,0.7)" }}>
            <table className="w-full text-[12.5px]" data-testid="blacklist-table">
              <thead className="bg-white/5">
                <tr className="text-left text-[10.5px] uppercase tracking-widest text-slate-400">
                  <th className="px-4 py-3 font-bold">Email</th>
                  <th className="px-4 py-3 font-bold">Motivo</th>
                  <th className="px-4 py-3 font-bold">Falhas</th>
                  <th className="px-4 py-3 font-bold">Adicionado em</th>
                  <th className="px-4 py-3 font-bold">Por</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Carregando…
                  </td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500" data-testid="blacklist-empty">
                    Nenhum email na blacklist.
                  </td></tr>
                )}
                {items.map((row, i) => (
                  <tr key={row.id} className="border-t border-white/5 hover:bg-white/5"
                      data-testid={`blacklist-row-${i}`}>
                    <td className="px-4 py-3 font-mono text-red-300">{row.email}</td>
                    <td className="px-4 py-3 text-slate-300">{row.reason || "—"}</td>
                    <td className="px-4 py-3 tabular-nums">{row.failure_count || 0}</td>
                    <td className="px-4 py-3 text-slate-400 text-[11.5px]">
                      {row.added_at ? new Date(row.added_at).toLocaleString("pt-BR") : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-[11.5px]">{row.added_by || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => remove(row)}
                              data-testid={`blacklist-remove-${i}`}
                              title="Remover da blacklist"
                              className="w-7 h-7 rounded flex items-center justify-center bg-red-500/10 hover:bg-red-500/30 text-red-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </main>
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
             onClick={() => !adding && setAddOpen(false)}
             data-testid="blacklist-add-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 p-6"
               style={{ background: "rgba(15,23,42,0.95)" }}
               onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[16px] font-extrabold text-white mb-4">
              Adicionar email à blacklist
            </h3>
            <div className="space-y-3">
              <input value={addEmail} onChange={(e) => setAddEmail(e.target.value)}
                     placeholder="destinatario@exemplo.com"
                     data-testid="blacklist-add-email"
                     className="w-full h-10 px-3 rounded-lg bg-black/40 border border-white/10 text-[13px] text-white focus:border-[#DC2626] outline-none" />
              <input value={addReason} onChange={(e) => setAddReason(e.target.value)}
                     placeholder="Motivo (opcional)"
                     data-testid="blacklist-add-reason"
                     className="w-full h-10 px-3 rounded-lg bg-black/40 border border-white/10 text-[13px] text-white focus:border-[#DC2626] outline-none" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setAddOpen(false)} disabled={adding}
                      className="h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 hover:bg-white/5">
                Cancelar
              </button>
              <button onClick={addManual} disabled={adding || !addEmail.includes("@")}
                      data-testid="blacklist-add-confirm"
                      className="h-9 px-4 rounded-lg text-[12px] font-bold text-white disabled:opacity-50 inline-flex items-center gap-1.5"
                      style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                {adding ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adicionando…</> : "Adicionar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
